import React from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { makeStyles, Typography, CircularProgress } from '@material-ui/core'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import LightbulbOutlinedIcon from '@material-ui/icons/EmojiObjectsOutlined'

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: '10px 12px',
    borderRadius: 4,
    border: '1px solid var(--color-neutral-200, #e5e7eb)',
    background: 'var(--opal-surface-default, #fff)'
  },
  iconCol: {
    width: 20,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 2,
    flexShrink: 0
  },
  body: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    minWidth: 0
  },
  title: {
    fontSize: 13,
    fontWeight: 'var(--font-weight-semibold, 600)',
    color: 'var(--color-neutral-800, #1f2937)',
    lineHeight: 1.35
  },
  detail: {
    fontSize: 12,
    color: 'var(--color-neutral-600, #4b5563)',
    whiteSpace: 'pre-wrap',
    lineHeight: 1.45
  },
  meta: {
    fontSize: 11,
    color: 'var(--color-neutral-400, #9ca3af)',
    fontVariantNumeric: 'tabular-nums'
  },
  iconNote: { color: 'var(--color-neutral-400, #9ca3af)' },
  iconComplete: { color: 'var(--color-success-600, #16a34a)' },
  iconFailed: { color: 'var(--color-danger-600, #dc2626)' },
  failedRoot: {
    background: 'var(--color-danger-050, #fef2f2)',
    borderColor: 'var(--color-danger-200, #fecaca)'
  }
}))

export default function StepRow ({ step }) {
  const classes = useStyles()
  if (!step) return null

  const { status, title, detail, executionMs, fetchCount } = step

  return (
    <div className={clsx(classes.root, { [classes.failedRoot]: status === 'failed' })}>
      <div className={classes.iconCol}>
        {status === 'running' && <CircularProgress size={14} />}
        {status === 'complete' && <CheckCircleIcon fontSize='small' className={classes.iconComplete} />}
        {status === 'failed' && <ErrorOutlineIcon fontSize='small' className={classes.iconFailed} />}
        {status === 'note' && <LightbulbOutlinedIcon fontSize='small' className={classes.iconNote} />}
      </div>
      <div className={classes.body}>
        <Typography component='div' className={classes.title}>{title}</Typography>
        {detail && (
          <Typography component='div' className={classes.detail}>{detail}</Typography>
        )}
        {(executionMs != null || fetchCount != null) && (
          <Typography component='div' className={classes.meta}>
            {[
              fetchCount != null ? `${fetchCount} fetch${fetchCount === 1 ? '' : 'es'}` : null,
              executionMs != null ? `${executionMs}ms` : null
            ].filter(Boolean).join(' · ')}
          </Typography>
        )}
      </div>
    </div>
  )
}

StepRow.propTypes = {
  step: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    detail: PropTypes.string,
    status: PropTypes.oneOf(['note', 'running', 'complete', 'failed']),
    executionMs: PropTypes.number,
    fetchCount: PropTypes.number
  })
}
